import { useState, useRef, useEffect } from 'react'
import { X, ZoomIn, ZoomOut, Maximize2, RotateCcw } from 'lucide-react'

interface ImageViewerModalProps {
  imageUrl: string
  imageName?: string
  onClose: () => void
}

export function ImageViewerModal({ imageUrl, imageName, onClose }: ImageViewerModalProps) {
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const imageRef = useRef<HTMLDivElement>(null)

  // Zoom in/out
  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 5))
  }

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5))
  }

  // Reset view
  const handleReset = () => {
    setScale(1)
    setPosition({ x: 0, y: 0 })
  }

  // Fit to screen
  const handleFitToScreen = () => {
    setScale(1)
    setPosition({ x: 0, y: 0 })
  }

  // Mouse wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.1 : 0.1
    setScale(prev => Math.max(0.5, Math.min(5, prev + delta)))
  }

  // Mouse drag to pan
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale <= 1) return // Only allow panning when zoomed in
    setIsDragging(true)
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === '+' || e.key === '=') handleZoomIn()
      if (e.key === '-' || e.key === '_') handleZoomOut()
      if (e.key === '0') handleReset()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Controls */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-neutral-800/90 backdrop-blur-sm rounded-lg px-4 py-2 flex items-center gap-4 z-10">
        <div className="text-sm text-neutral-300 font-medium">
          {imageName || '이미지 뷰어'}
        </div>
        <div className="h-4 w-px bg-neutral-600" />
        <div className="text-xs text-neutral-400">
          {Math.round(scale * 100)}%
        </div>
      </div>

      {/* Zoom Controls */}
      <div className="absolute top-4 right-4 bg-neutral-800/90 backdrop-blur-sm rounded-lg p-2 flex flex-col gap-2 z-10">
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleZoomIn()
          }}
          className="p-2 hover:bg-neutral-700 rounded transition-colors"
          title="확대 (+)"
        >
          <ZoomIn className="w-5 h-5 text-neutral-300" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleZoomOut()
          }}
          className="p-2 hover:bg-neutral-700 rounded transition-colors"
          title="축소 (-)"
        >
          <ZoomOut className="w-5 h-5 text-neutral-300" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleFitToScreen()
          }}
          className="p-2 hover:bg-neutral-700 rounded transition-colors"
          title="화면 맞춤 (0)"
        >
          <Maximize2 className="w-5 h-5 text-neutral-300" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleReset()
          }}
          className="p-2 hover:bg-neutral-700 rounded transition-colors"
          title="초기화"
        >
          <RotateCcw className="w-5 h-5 text-neutral-300" />
        </button>
      </div>

      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 left-4 p-2 bg-neutral-800/90 backdrop-blur-sm hover:bg-neutral-700 rounded-lg transition-colors z-10"
        title="닫기 (ESC)"
      >
        <X className="w-6 h-6 text-neutral-300" />
      </button>

      {/* Image Container */}
      <div
        ref={imageRef}
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          cursor: isDragging ? 'grabbing' : (scale > 1 ? 'grab' : 'default')
        }}
      >
        <img
          src={imageUrl}
          alt={imageName || 'Image'}
          className="max-w-none transition-transform select-none"
          draggable={false}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: 'center center'
          }}
        />
      </div>

      {/* Hints */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-neutral-800/90 backdrop-blur-sm rounded-lg px-4 py-2 z-10">
        <p className="text-xs text-neutral-400">
          💡 마우스 휠: 확대/축소 | 드래그: 이동 | ESC: 닫기 | +/-: 줌 | 0: 초기화
        </p>
      </div>
    </div>
  )
}
